import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { meKey } from './auth';
import { setActiveWorkspaceId } from './workspace-scope';
import type { components } from './schema';
import type { Me } from './types';

type S = components['schemas'];
export type Invite = S['Invite'];
export type SentInvite = S['SentInvite'];
export type InvitePreview = S['InvitePreview'];
export type InviteInput = S['InviteInput'];

const invitesKey = ['invites'] as const;

export function useInvites(enabled = true) {
  return useQuery({
    queryKey: invitesKey,
    enabled,
    queryFn: ({ signal }) => api<{ data: Invite[] }>('/invites', { signal }).then((r) => r.data),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: invitesKey });
    void qc.invalidateQueries({ queryKey: ['billing'] }); // pending invites hold seats
  };
}

export function useSendInvite() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: InviteInput) =>
      api<{ data: SentInvite }>('/invites', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useResendInvite() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ data: SentInvite }>(`/invites/${id}/resend`, { method: 'POST', body: {} }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useRevokeInvite() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/invites/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Public: what an invite link is for. */
export function useInvitePreview(token: string | null) {
  return useQuery({
    queryKey: ['invite-preview', token],
    enabled: !!token,
    retry: false,
    queryFn: ({ signal }) =>
      api<{ data: InvitePreview }>(`/auth/invites/${encodeURIComponent(token!)}`, { signal }).then((r) => r.data),
  });
}

/** Public: join with the invite; signs the new user in. */
export function useAcceptInvite(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; password: string }) => {
      setActiveWorkspaceId(null);
      return api<{ data: { user: Me } }>(`/auth/invites/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body: input,
      }).then((r) => r.data.user);
    },
    onSuccess: (user) => {
      qc.clear();
      qc.setQueryData(meKey, user);
    },
  });
}
